// @flow

import * as React from 'react'
import styled from '@emotion/styled'
import { ThemeProvider } from 'emotion-theming'
import Cookies from 'js-cookie'
import { Link } from 'gatsby'
import {
  Button,
  Hero,
  Icon,
  Card,
  CallToAction,
  Carousel,
  Section,
  text,
  hooks,
} from '@toggl/ui'
import { colors, fonts, media, themes } from '@toggl/style'
import IndexLayout from '../layouts'
import LayoutGlobal from '../components/LayoutGlobal'
import { Video } from 'components/video'
import { withPrefix } from 'gatsby'
import { Parallax } from 'react-scroll-parallax'

declare var globalThis: any

const HeroComponent = ({ children }) => {
  return (
    <Hero.Wrapper
      bgImage={require('assets/homepage-hero-bg.jpg')}
      bgVideo={require('assets/bg-video.mp4')}
    >
      {children}
      <ThemeProvider theme={themes.track}>
        <Hero.ContentWrapper>
          <Hero.Title>
            It’s time <br />
            to get your
            <br /> team on <Rotalic>Track</Rotalic>.
          </Hero.Title>

          <LogoWrapper>
            <Icon.Track />
          </LogoWrapper>

          <Hero.Text>Reliable time tracking loved by 5M+ users</Hero.Text>

          <Hero.CtaSection>
            <Link to={'/track/signup/'}>
              <Button.Primary>Sign-up for free</Button.Primary>
            </Link>
            <Link to={'/track/'}>
              <Button.Secondary color={colors.almostWhite}>
                Or discover more
              </Button.Secondary>
            </Link>
          </Hero.CtaSection>
        </Hero.ContentWrapper>
      </ThemeProvider>
    </Hero.Wrapper>
  )
}

const IndexPage = () => {
  const [offsetTop, setOffsetTop] = React.useState(0)
  const [topBarOpen, setTopBarOpen] = React.useState(false)
  const topbarRef = React.useRef(null)

  React.useEffect(() => {
    setOffsetTop(topBarOpen ? topbarRef?.current?.offsetHeight : 0)
  }, [topbarRef, topBarOpen])

  React.useEffect(() => {
    const cookie = Cookies.get('topbar-closed')
    setTopBarOpen(!cookie)
  }, [setTopBarOpen])

  const isDesktop = hooks.useMediaQuery(media.mqbp[1])

  return (
    <IndexLayout
      title={'Toggl: Simple & Beautiful Tools that Help Teams Work Better'}
      description={
        'We take the stress out of time tracking, project-planning, and hiring. Designed by and for teams that work from anywhere.'
      }
      ogImage={require('assets/toggl-global-main.jpg')}
      favicon={require('assets/favicon-global.png')}
    >
      {topBarOpen && (
        <StyledTopbar ref={topbarRef}>
          <TopbarWrapper>
            <p>
              <span>Toggl is now Toggl Track! </span>We're still the Toggl you
              know and love, but we've welcomed Toggl Plan and Toggl Hire into
              the family 🤗{' '}
              <a
                href="https://toggl.com/blog/toggl-rebrand-toggltrack/"
                target="_blank"
                rel="noreferrer"
              >
                Read more.
              </a>
            </p>
            <span
              onClick={() => {
                Cookies.set('topbar-closed', true)
                setTopBarOpen(false)
              }}
            >
              Close
            </span>
          </TopbarWrapper>
        </StyledTopbar>
      )}

      <LayoutGlobal offsetTop={offsetTop} Hero={HeroComponent}>
        <Section.Block background={colors.darkPurple} flex>
          <CardWrapper>
            <ThemeProvider theme={themes.plan}>
              <Card.Product
                Logo={Icon.Plan}
                title={'Beautifully simple project planning'}
                video={<Video src={require('assets/toggl-plan.mp4')} />}
                Cta={() => (
                  <a href={withPrefix('/plan/')}>
                    <Button.Primary>
                      {isDesktop ? 'Start planning for free' : 'Start planning'}
                    </Button.Primary>
                  </a>
                )}
                discoverMore={
                  <a href={withPrefix('/plan/product')}>or discover features</a>
                }
              />
            </ThemeProvider>
            <ThemeProvider theme={themes.track}>
              <Card.Product
                Logo={Icon.Track}
                title={'Effortless time tracking and reporting'}
                video={<Video src={require('assets/toggl-track.mp4')} />}
                Cta={() => (
                  <Link to="/track/">
                    <Button.Primary>
                      {isDesktop ? 'Start tracking for free' : 'Start tracking'}
                    </Button.Primary>
                  </Link>
                )}
                discoverMore={
                  <Link to="/track/features/">or discover features</Link>
                }
              />
            </ThemeProvider>
            <ThemeProvider theme={themes.hire}>
              <Card.Product
                Logo={Icon.Hire}
                title={'Smarter hiring with skills tests'}
                video={<Video src={require('assets/toggl-hire.mp4')} />}
                Cta={() => (
                  <a href={withPrefix('/hire/')}>
                    <Button.Primary>
                      {isDesktop ? 'Start hiring for free' : 'Start hiring'}
                    </Button.Primary>
                  </a>
                )}
                discoverMore={
                  <a href={withPrefix('/hire/product')}>or discover features</a>
                }
              />
            </ThemeProvider>
          </CardWrapper>
        </Section.Block>
        <Section.Block center background={colors.darkPurple}>
          <CtaOneWrapper>
            <CallToAction
              size={1}
              title={
                <span>
                  Work <Rotalic>Smarter</Rotalic>,
                  <br />
                  Live Happier
                </span>
              }
              subTitle={
                <>
                  <span>
                    Our global team comes together every day to move our mission
                    forward - making simple, elegant tools to help you be
                    productive without the stress
                  </span>
                  <MissionLinkWrapper>
                    <Link to="/mission/">
                      <Button.Secondary color={colors.beige}>
                        See what drives us
                      </Button.Secondary>
                    </Link>
                  </MissionLinkWrapper>
                </>
              }
            />

            <StyledImage
              src={
                isDesktop
                  ? require('assets/killstress-dark.gif')
                  : require('assets/killstress-placeholder.png')
              }
              alt={'Work smarter, live happier'}
              loading="lazy"
            />
          </CtaOneWrapper>
        </Section.Block>

        <ArticleSection topBg={colors.darkPurple} bottomBg={colors.fadedPurple}>
          <Section.Diagonal.Wrapper background={colors.fadedPurple}>
            <Section.Diagonal.Content>
              <Wrapper>
                <CtaParagraphSectionSecond>
                  <Parallax y={isDesktop ? [-30, 30] : [-20, 15]}>
                    <CallToAction
                      size={2}
                      title={<span>Knowledge is Power</span>}
                      subTitle={
                        <span>
                          Get your remote-working, time tracking,
                          project-planning, and hiring best practices here!
                          Straight from the experts at Toggl.
                        </span>
                      }
                      Cta={() => (
                        <Link to={'https://toggl.com/blog/'}>
                          <Button.Primary>See More on Our Blog</Button.Primary>
                        </Link>
                      )}
                      image={
                        isDesktop
                          ? require('assets/megaphone-dark.gif')
                          : require('assets/megaphone.png')
                      }
                    />
                  </Parallax>
                </CtaParagraphSectionSecond>
              </Wrapper>

              <Carousel.DotMobile>
                <Card.ReadMore
                  title={'How To Work From Home'}
                  subtitle={
                    'Everything we’ve learned from the last seven years of remote work'
                  }
                  link={
                    <a
                      href="https://toggl.com/work-from-home/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button.Secondary>Read more</Button.Secondary>
                    </a>
                  }
                  image={
                    <img
                      src={require('assets/resource-work-from-home.png')}
                      alt={'Toggl Work From Home Hub'}
                      loading="lazy"
                    />
                  }
                />
                <Card.ReadMore
                  title={'The Expectations Game'}
                  subtitle={
                    'The secret behind any happy collaboration usually lies in the skill of managing expectations'
                  }
                  link={
                    <a
                      href="https://toggl.com/plan/blueprint-the-expectations-game/index"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button.Secondary>Read more</Button.Secondary>
                    </a>
                  }
                  image={
                    <img
                      src={require('assets/resource-toggl-plan-blueprint.png')}
                      alt={'The Expectations Game Blueprint'}
                      loading="lazy"
                    />
                  }
                />
                <Card.ReadMore
                  title={'Hiring Remotely'}
                  subtitle={'Hiring Remotely: A Practical Guide'}
                  link={
                    <a
                      href="https://ebook.hire.toggl.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button.Secondary>Read more</Button.Secondary>
                    </a>
                  }
                  image={
                    <img
                      src={require('assets/resource-hiring-remotely.png')}
                      alt={'Hiring Remotely Online Guide'}
                      loading="lazy"
                    />
                  }
                />
              </Carousel.DotMobile>
            </Section.Diagonal.Content>
          </Section.Diagonal.Wrapper>
        </ArticleSection>
      </LayoutGlobal>
    </IndexLayout>
  )
}

const CtaOneWrapper = styled.div`
  padding-bottom: 50px;
  ${media.mq[1]} {
    padding-bottom: 100px;
  }
`

const StyledImage = styled.img`
  width: 100%;
  transform: translateY(-30px);
  ${media.mq[1]} {
    max-width: 700px;
    margin-bottom: 30px;
    transform: translateY(-20px);
  }
`

const StyledTopbar = styled.div`
  min-height: 60px;
  background: ${colors.yellow};
  padding: 20px;

  p,
  a {
    ${text.paragraph3};
    color: ${colors.darkPurple};
  }

  a {
    text-decoration: underline;
    display: inline-block;
    color: ${colors.darkPurple};
  }

  > div > span {
    margin-top: 10px;
    display: block;
    cursor: pointer;
  }

  ${media.mq[1]} {
    > div > span {
      margin-top: 0;
      margin-left: 20px;
    }
  }

  position: relative;
`
const TopbarWrapper = styled.div`
  ${media.mq[1]} {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;

    p > span {
      font-weight: bold;
    }
  }
`

const CardWrapper = styled.div`
  ${media.mq[1]} {
    display: flex;
    flex-direction: column;
    margin: auto;
  }
  ${media.mq[2]} {
    flex-direction: row;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: auto;
`

const MissionLinkWrapper = styled.span`
  margin-top: 20px;
  display: block;
`

const CtaParagraphSection = styled.div`
  padding: 0 30px 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    ${media.mq[1]} {
      max-width: 840px;
    }
  }

  ${media.mq[1]} {
    padding: 0;
  }
`

const CtaParagraphSectionSecond = styled(CtaParagraphSection)`
  margin-top: -100px;
  padding-bottom: 70px;
  ${media.mq[1]} {
    margin-top: -250px;
    padding-bottom: 150px;
  }
`

const ArticleSection = styled(Section.Diagonal.Background)`
  > div {
    overflow: visible;
    > div {
      padding: 50px 0 0;
    }
    ${media.mq[1]} {
      padding: 50px 30px 25px;
    }
  }
`

const LogoWrapper = styled.div`
  svg {
    width: 120px;
  }
  ${media.mq[1]} {
    svg {
      width: 167px;
    }
  }
`

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${(p) => p.theme.primary};
`

export default IndexPage
